/* eslint-disable no-console */
import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
} from 'react';

export interface Customer {
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  dateOfBirth: string;
  ssn1: string;
  ssn2: string;
  ssn3: string;
  agreementNumber: string;
  phoneNumber: string;
  offset?: number;
  limit?: number;
}

interface SearchResult {
  checked?: number;
  customerId: string;
  firstName: string;
  lastName: string;
  dateOfBirthEnc: string;
  taxIdEnc: string;
  city: string;
  state: string;
  zipcode: string;
  plus4: string;
  daysPastDue: number;
  phoneNumber: string;
  route: string;
  dueDate: string;
  commitment: string;
  contactsTotal: number;
  contactsToday: number;
  offset?: number;
  limit?: number;
}
export interface CustomerRecordState {
  customerBasicInformation: Customer;
  stateOptions: { value: string; label: string }[];
  searchResults: SearchResult[];
  queuedCustomers: SearchResult[];
  errorMessage: string;
  isSearchButtonEnabled: boolean;
  selectedCustomerQueue: SearchResult[];
  hasMore: boolean;
  searchLoading: boolean;
  offset: number;
  searchPerformed: boolean;
}
interface CustomerRecordDispatchState {
  setCustomerBasicInformation: (info: Partial<Customer>) => void;
  setStateOptions: (options: { value: string; label: string }[]) => void;
  setSearchResults: (results: SearchResult[], append?: boolean) => void;
  addToQueue: (customer: SearchResult[], append: boolean) => void;
  setErrorMessage: (message: string) => void;
  resetForm: () => void;
  selectedCustomer: (customer: SearchResult[]) => void;
  setSearchListLoading: (loading: boolean) => void;
  setOffsetValue: (message: number) => void;
  setMainSearch: (value: boolean) => void;
}

export const CustomerRecordStateContext = createContext<CustomerRecordState>(
  {} as CustomerRecordState
);
export const CustomerRecordDispatchStateContext =
  createContext<CustomerRecordDispatchState>({} as CustomerRecordDispatchState);

export const useCustomerRecordState = () =>
  React.useContext(CustomerRecordStateContext);
export const useCustomerRecordDispatch = () =>
  React.useContext(CustomerRecordDispatchStateContext);

const defaultCustomer: Customer = {
  firstName: '',
  lastName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '0',
  zip: '',
  dateOfBirth: '',
  ssn1: '',
  ssn2: '',
  ssn3: '',
  agreementNumber: '',
  phoneNumber: '',
};

// Provider component
const CustomerRecordProvider = ({ children }: { children: ReactNode }) => {
  const [customerBasicInformation, setCustomerBasicInformationState] =
    useState<Customer>(defaultCustomer);
  const [stateOptions, setStateOptionsState] = useState<
    CustomerRecordState['stateOptions']
  >([]);
  const [searchResults, setSearchResultsState] = useState<SearchResult[]>([]);
  const [queuedCustomers, setQueuedCustomers] = useState<SearchResult[]>([]);
  const [formValidationErrors, setFormValidationErrorsState] = useState<{
    [key: string]: string;
  }>({});
  const [errorMessage, setErrorMessageState] = useState<string>('');
  const [isSearchButtonEnabled, setIsSearchButtonEnabled] =
    useState<boolean>(false);
  const [selectedCustomerQueue, setSelectedCustomerQueue] = useState<
    SearchResult[]
  >([]);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [offset, setOffset] = useState<number>(0);
  const [searchPerformed, setSearchPerformed] = useState<boolean>(false);

  const setCustomerBasicInformation = (info: Partial<Customer>) => {
    setCustomerBasicInformationState((prev) => ({ ...prev, ...info }));
  };

  const setStateOptions = (options: { value: string; label: string }[]) => {
    setStateOptionsState(options);
  };

  const setSearchResults = (results: SearchResult[], append?: boolean) => {
    const updatedResults = results.map((result) => ({
      ...result,
      checked: result.checked ?? 0,
    }));

    setHasMore(updatedResults.length > 0);

    if (append) {
      setSearchResultsState((prev) => [...prev, ...updatedResults]);
    } else {
      setSearchResultsState(updatedResults);
    }
  };

  const addToQueue = (customers: SearchResult[], append: boolean) => {
    setQueuedCustomers((prev) =>
      append ? [...prev, ...customers] : customers
    );
  };

  const selectedCustomer = (customers: SearchResult[]) => {
    setSelectedCustomerQueue(customers);
  };

  const setErrorMessage = (message: string) => {
    setErrorMessageState(message);
  };

  const resetForm = () => {
    setCustomerBasicInformationState(defaultCustomer);
    setFormValidationErrorsState({});
    setErrorMessageState('');
    setIsSearchButtonEnabled(false);
  };

  const setOffsetValue = (message: number) => {
    setOffset(message);
  };

  const setMainSearch = (message: boolean) => {
    setSearchPerformed(message);
  };

  useEffect(() => {
    const {
      firstName,
      lastName,
      dateOfBirth,
      ssn1,
      ssn2,
      ssn3,
      phoneNumber,
      agreementNumber,
      addressLine1,
      addressLine2,
      zip,
      city,
      state,
    } = customerBasicInformation;

    const isAnyFieldValid =
      firstName.length >= 3 ||
      lastName.length >= 3 ||
      firstName.length + lastName.length >= 3 ||
      dateOfBirth !== '' ||
      ssn1.length + ssn2.length + ssn3.length >= 9 ||
      phoneNumber.length === 14 ||
      agreementNumber.length > 4 ||
      addressLine1.length >= 5 ||
      addressLine2.length >= 5 ||
      zip.length >= 5 ||
      city.length > 3 ||
      state !== '0';

    console.log('isAnyFieldValid:', isAnyFieldValid);
    setIsSearchButtonEnabled(!isAnyFieldValid);
  }, [customerBasicInformation]);

  const setSearchListLoading = (loading: boolean) => setSearchLoading(loading);

  const stateValue = useMemo(
    () => ({
      customerBasicInformation,
      stateOptions,
      searchResults,
      queuedCustomers,
      formValidationErrors,
      errorMessage,
      isSearchButtonEnabled,
      selectedCustomerQueue,
      hasMore,
      searchLoading,
      offset,
      searchPerformed,
    }),
    [
      customerBasicInformation,
      stateOptions,
      searchResults,
      queuedCustomers,
      formValidationErrors,
      errorMessage,
      isSearchButtonEnabled,
      selectedCustomerQueue,
      hasMore,
      searchLoading,
      offset,
      searchPerformed,
    ]
  );

  const dispatchValue = useMemo(
    () => ({
      setCustomerBasicInformation,
      setStateOptions,
      setSearchResults,
      addToQueue,
      setErrorMessage,
      resetForm,
      setSearchListLoading,
      selectedCustomer,
      setOffsetValue,
      setMainSearch,
    }),
    []
  );

  return (
    <CustomerRecordStateContext.Provider value={stateValue}>
      <CustomerRecordDispatchStateContext.Provider value={dispatchValue}>
        {children}
      </CustomerRecordDispatchStateContext.Provider>
    </CustomerRecordStateContext.Provider>
  );
};

export default CustomerRecordProvider;
